import {effect, Injectable} from '@angular/core';
import {
  AppNotification,
  AppNotificationStatus,
  AppNotificationType,
  NotificationService
} from '@frednh/ai-agentic-core';
import {CompanyRefreshScope, CompanyService} from './company-service';
import {OfferingService} from './offering-service';
import {UserManagementService} from './user-management-service';

@Injectable({
  providedIn: 'root'
})
export class CrmNotificationRefreshService {
  constructor(
    private readonly notificationService: NotificationService,
    private readonly companyService: CompanyService,
    private readonly offeringService: OfferingService,
    private readonly userManagementService: UserManagementService
  ) {
    effect(() => {
      const notification = this.notificationService.incomingNotification();
      if (!this.shouldRefresh(notification)) {
        return;
      }

      if (!notification.code) {
        return;
      }
      const refreshPlan = TOOL_REFRESH_PLAN[notification.code];
      if (!refreshPlan) {
        return;
      }

      if (refreshPlan.companyScope) {
        this.companyService.refreshAgentDrivenAreas(refreshPlan.companyScope);
      }
      if (refreshPlan.offerings) {
        this.offeringService.getOfferings();
      }
      if (refreshPlan.currentProfile) {
        this.userManagementService.refreshCurrentProfile();
      }
      if (refreshPlan.selectedGroupUsers) {
        this.userManagementService.refreshSelectedGroupUsers();
      }
    });
  }

  private shouldRefresh(notification: AppNotification | null): notification is AppNotification {
    return notification != null
      && notification.type === AppNotificationType.MCP_TOOL_CALL
      && notification.status == AppNotificationStatus.DONE;
  }
}

interface RefreshPlan {
  companyScope?: CompanyRefreshScope;
  offerings?: boolean;
  currentProfile?: boolean;
  selectedGroupUsers?: boolean;
}

const COMPANY_LIST_AND_DETAIL: CompanyRefreshScope = {
  companies: true,
  selectedCompanyDetail: true
};

const COMPANY_NAME_AWARE_REFRESH: CompanyRefreshScope = {
  companies: true,
  actionFeed: true,
  opportunityFeed: true,
  groupMemberAssignments: true,
  selectedCompanyDetail: true
};

const COMPANY_DETAIL_ONLY_REFRESH: CompanyRefreshScope = {
  selectedCompanyDetail: true
};

const CUSTOMER_REFRESH: CompanyRefreshScope = {
  companies: true,
  actionFeed: true,
  opportunityFeed: true,
  groupMemberAssignments: true,
  selectedCompanyDetail: true
};

const CONTRACT_REFRESH: CompanyRefreshScope = {
  companies: true,
  actionFeed: true,
  opportunityFeed: true,
  groupMemberAssignments: true,
  selectedCompanyDetail: true
};

const ACTION_REFRESH: CompanyRefreshScope = {
  companies: true,
  actionFeed: true,
  opportunityFeed: true,
  selectedCompanyDetail: true
};

const COMMENT_REFRESH: CompanyRefreshScope = {
  opportunityFeed: true,
  selectedCompanyDetail: true
};

const OFFERING_REFRESH: CompanyRefreshScope = {
  actionFeed: true,
  opportunityFeed: true,
  groupMemberAssignments: true,
  selectedCompanyDetail: true
};

const USER_PROFILE_REFRESH: RefreshPlan = {
  currentProfile: true,
  selectedGroupUsers: true
};

const TOOL_REFRESH_PLAN: Record<string, RefreshPlan> = {
  createCompany: {companyScope: {companies: true}},
  updateCompanyMainInformation: {companyScope: COMPANY_NAME_AWARE_REFRESH},
  updateCompanyStatus: {companyScope: COMPANY_LIST_AND_DETAIL},
  updateCompanyStrategicInformation: {companyScope: COMPANY_DETAIL_ONLY_REFRESH},
  assignCompanyOwner: {companyScope: COMPANY_NAME_AWARE_REFRESH},
  setMainContact: {companyScope: COMPANY_DETAIL_ONLY_REFRESH},
  setParentCompany: {companyScope: COMPANY_LIST_AND_DETAIL},
  updateCompanyCommunication: {companyScope: COMPANY_LIST_AND_DETAIL},
  updateCompanyBusinessProfile: {companyScope: COMPANY_LIST_AND_DETAIL},
  updateCompanyLocation: {companyScope: COMPANY_LIST_AND_DETAIL},
  setCompanyTags: {companyScope: COMPANY_LIST_AND_DETAIL},
  addCompanyTags: {companyScope: COMPANY_LIST_AND_DETAIL},
  removeCompanyTags: {companyScope: COMPANY_LIST_AND_DETAIL},

  createCustomer: {companyScope: CUSTOMER_REFRESH},
  updateCustomer: {companyScope: CUSTOMER_REFRESH},
  deleteCustomer: {companyScope: CUSTOMER_REFRESH},

  createContract: {companyScope: CONTRACT_REFRESH},
  updateContract: {companyScope: CONTRACT_REFRESH},
  deleteContract: {companyScope: CONTRACT_REFRESH},

  createAction: {companyScope: ACTION_REFRESH},
  updateAction: {companyScope: ACTION_REFRESH},
  deleteAction: {companyScope: ACTION_REFRESH},

  createComment: {companyScope: COMMENT_REFRESH},
  updateComment: {companyScope: COMMENT_REFRESH},
  deleteComment: {companyScope: COMMENT_REFRESH},

  createOffering: {companyScope: OFFERING_REFRESH, offerings: true},
  updateOffering: {companyScope: OFFERING_REFRESH, offerings: true},
  deleteOffering: {companyScope: OFFERING_REFRESH, offerings: true},

  updateUserProfessionalProfile: USER_PROFILE_REFRESH,
  setUserSkillTags: USER_PROFILE_REFRESH,
  addUserSkillTags: USER_PROFILE_REFRESH,
  removeUserSkillTags: USER_PROFILE_REFRESH
};
